/**
 * Safe parsing for `YYYY-MM-DD` date-only strings.
 *
 * Why this exists:
 *   `new Date("2026-06-08")` is parsed by the spec as UTC midnight, then
 *   rendered in the browser's local timezone. For US users east of UTC
 *   that pushes the displayed day back to "2026-06-07" — so the date the
 *   user just saved appears one day earlier on the board.
 *
 * Strategy:
 *   - If the string matches `YYYY-MM-DD`, build the Date with explicit
 *     local year/month/day so the timezone never enters the picture.
 *   - Otherwise fall back to `new Date(raw)` so anything carrying an
 *     explicit time/zone (full ISO, RFC, …) still works.
 *
 * Returns `null` for blank/invalid input so callers can short-circuit
 * with a placeholder.
 */
export function parseLocalDate(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `6/8/2026` style display, timezone-safe for `YYYY-MM-DD` inputs. */
export function formatLocalDateMDY(raw) {
  const d = parseLocalDate(raw);
  if (!d) return '';
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
}
